import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './contexts/AuthContext';
import { EntityContextProvider } from './components/EntityContextSwitch';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { CoachingRoleProvider } from './contexts/CoachingRoleContext';
import ProtectedRoute from './components/ProtectedRoute';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import Layout from './components/Layout';
import LoginPage from './components/LoginPage';
import UserSettings from './components/Settings/UserSettings';
import { Chat } from './routes/Chat';
import { NotebookRoute } from './routes/Capture/Notebook';
import { Help } from './routes/Help';
import Care from './components/Care/Care';

import PlayerCardPage from './components/Care/PlayerCardPage';
import BehavioralWorkbench from './components/BehavioralWorkbench';
import theme from './theme';

// Lazy-loaded components
const CoachHome = lazy(() => import('./components/CoachHome/CoachHome'));
const OrgPlayerCard = lazy(() => import('./components/CoachHome/OrgPlayerCard'));
const PersonPlayerCard = lazy(() => import('./components/CoachHome/PersonPlayerCard'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const FaanaAgents = lazy(() => import('./routes/FaanaAgents'));
const ReportsPage = lazy(() => import('./components/Reports/ReportsPage'));

const LazyFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <CircularProgress color="secondary" />
  </Box>
);

function App() {
  return (
    <GlobalErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <UserPreferencesProvider>
            <EntityContextProvider>
              <CoachingRoleProvider>
              <BrowserRouter>
                <ToastContainer
                  position="bottom-right"
                  autoClose={5000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="colored"
                />
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />

                  {/* Protected routes */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<Layout />}>
                      {/* Core routes */}
                      <Route path="/reports" element={<Suspense fallback={<LazyFallback />}><ReportsPage /></Suspense>} />
                      <Route path="/settings" element={<UserSettings />} />

                      {/* Legacy redirects */}
                      <Route path="/dashboard" element={<Navigate to="/reports" replace />} />
                      <Route path="/insights" element={<Navigate to="/reports" replace />} />
                      <Route path="/chat" element={<Chat />} />

                      {/* Behavioral Workbench */}
                      <Route path="/behavioral-workbench" element={<BehavioralWorkbench />} />

                      {/* Help */}
                      <Route path="/help" element={<Help />} />

                      {/* Care (CCM) routes */}
                      <Route path="/care" element={<Care />} />
                      <Route path="/care/player-card/:entityId" element={<PlayerCardPage />} />

                      {/* Coach routes */}
                      <Route path="/coach-home" element={<Suspense fallback={<LazyFallback />}><CoachHome /></Suspense>} />
                      <Route path="/coach-home/org/:entityId" element={<Suspense fallback={<LazyFallback />}><OrgPlayerCard /></Suspense>} />
                      <Route path="/coach-home/org/:entityId/leader/:leaderId" element={<Suspense fallback={<LazyFallback />}><PersonPlayerCard /></Suspense>} />

                      {/* Admin routes */}
                      <Route path="/admin" element={<Suspense fallback={<LazyFallback />}><AdminDashboard /></Suspense>} />
                      <Route path="/faana-agents" element={<Suspense fallback={<LazyFallback />}><FaanaAgents /></Suspense>} />

                      {/* Capture routes */}
                      <Route path="/capture">
                        <Route index element={<Navigate to="/capture/notebook" replace />} />
                        <Route path="audio" element={<Navigate to="/capture/notebook" replace />} />
                        <Route path="upload" element={<Navigate to="/capture/notebook" replace />} />
                        <Route path="notebook" element={<NotebookRoute />} />
                      </Route>

                      {/* Default redirect to chat */}
                      <Route path="/" element={<Navigate to="/chat" replace />} />
                    </Route>
                  </Route>
                </Routes>
              </BrowserRouter>
              </CoachingRoleProvider>
            </EntityContextProvider>
          </UserPreferencesProvider>
        </AuthProvider>
      </ThemeProvider>
    </GlobalErrorBoundary>
  );
}

export default App;